import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { IconButton } from '@mui/material';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { Stream } from '../../Models/Stream';
import VideoCard from "./VideoCard";

const GAP = 16;
const ITEM_WIDTH = 260;
const ITEM_HEIGHT = 270;

type Props = {
  streams: Stream[];
  savedStreams: Stream[];
};

const VideoCarousel: React.FC<Props> = ({streams, savedStreams}) => {
  const [itemsPerRow, setItemsPerRow] = useState(1);
  const [currentPage, setCurrentPage] = useState(0);
  const [direction, setDirection] = useState(0); // +1 for next, -1 for prev
  const containerRef = useRef<HTMLDivElement>(null);

  const totalPages = Math.ceil(streams.length / itemsPerRow);

  const updateItemsPerRow = () => {
    if (containerRef.current) {
      const containerWidth = containerRef.current.offsetWidth;
      const count = Math.floor((containerWidth + GAP) / (ITEM_WIDTH + GAP));
      setItemsPerRow(Math.max(count, 1));
    }
  };

  useEffect(() => {
    updateItemsPerRow();
    window.addEventListener('resize', updateItemsPerRow);
    return () => window.removeEventListener('resize', updateItemsPerRow);
  }, []);

  useEffect(() => {
    if (currentPage >= totalPages) {
      setCurrentPage(0);
    }
  }, [itemsPerRow, totalPages, currentPage]);

  const startIndex = currentPage * itemsPerRow;
  const visibleItems = streams.slice(startIndex, startIndex + itemsPerRow + 1);

  // Calculate the width of the row dynamically
  const rowWidth = itemsPerRow * ITEM_WIDTH + (itemsPerRow - 1) * GAP;

  const IsStreamSaved = (Id: number): boolean => {
    return savedStreams.some(stream => stream.id === Id);
  };

  return (
    <div>
      <div
        ref={containerRef}
        style={{
          position: 'relative',
          overflow: 'hidden',
          padding: '16px',
          height: '100%',
        }}
      >
        <AnimatePresence initial={false} custom={direction}>
          <motion.div
            key={currentPage}
            custom={direction}
            initial="enter"
            animate="center"
            exit="exit"
            variants={{
              enter: (dir) => ({
                x: dir > 0 ? 300 : -300,
                opacity: 0,
                position: 'absolute',
              }),
              center: {
                x: 0,
                opacity: 1,
                position: 'relative',
                transition: { duration: 0.3 },
              },
              exit: (dir) => ({
                x: dir > 0 ? -300 : 300,
                opacity: 0,
                position: 'absolute',
                transition: { duration: 0.3 },
              }),
            }}
            style={{
              display: 'flex',
              gap: `${GAP}px`,
              minWidth: 0,  // Prevent flex shrinkage
              width: `${rowWidth}px`, // Dynamically set row width
            }}
          >
            {visibleItems.map((item) => (
              <div
                key={item.id}
                style={{
                  width: `${ITEM_WIDTH}px`, // Fixed width for items
                  height: `${ITEM_HEIGHT}px`,
                  aspectRatio: '4 / 3',
                  backgroundColor: '#66bb6a',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '20px',
                  color: '#fff',
                  borderRadius: '8px',
                  flex: '0 0 auto', // Prevent items from shrinking
                  minWidth: 0, // Ensure items don't overflow
                }}
              >
                <VideoCard stream={item} saved={IsStreamSaved(item.id)} goBackUrl={`/videos`}/>
              </div>
            ))}
          </motion.div>
        </AnimatePresence>

        <IconButton onClick={() => {
            setDirection(-1);
            setCurrentPage((prev) => Math.max(prev - 1, 0));
          }}
          disabled={currentPage === 0}
          style={{
            position: 'absolute',
            left: '0',
            top: '50%',
            transform: 'translateY(-50%)',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            color: 'white',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
            borderRadius: '50%',
            zIndex: 1,
            display: currentPage === 0 ? 'none' : 'flex',
          }}>
          <KeyboardArrowLeftIcon />
        </IconButton>

        <IconButton onClick={() => {
            setDirection(1);
            setCurrentPage((prev) => Math.min(prev + 1, totalPages - 1));
          }}
          disabled={currentPage >= totalPages - 1}
          style={{
            position: 'absolute',
            right: '0',
            top: '50%',
            transform: 'translateY(-50%)',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            color: 'white',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
            borderRadius: '50%',
            zIndex: 1,
            display: currentPage >= totalPages - 1 ? 'none' : 'flex',
          }}>
          <KeyboardArrowRightIcon />
        </IconButton>


      </div>
    </div>
  );
};

export default VideoCarousel;
